import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, CheckCircle2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { DEPARTMENTS, ITEM_TYPES, CONDITIONS, PHOTO_SLOTS, TRADE_IN_STEPS } from "../lib/catalog";
import { Overline } from "../components/Reveal";

const STEP_LABELS = ["Department", "Item Type", "Brand", "Details", "Condition", "Photographs"];

export default function TradeInFlow() {
  const [step, setStep] = useState(0);
  const [brands, setBrands] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    gender: "", item_type: "", brand_selected: "", brand_name: "",
    size: "", color: "", material: "", purchase_year: "", years_used: "",
    times_worn: "", purchase_price: "", original_mrp: "", condition: "", condition_notes: "",
  });
  const [photos, setPhotos] = useState({});
  const fileRefs = useRef({});

  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const readPhoto = (slot, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhotos((p) => ({ ...p, [slot]: reader.result }));
    reader.readAsDataURL(file);
  };

  const canNext = () => {
    if (step === 0) return !!form.gender;
    if (step === 1) return !!form.item_type;
    if (step === 2) return !!form.brand_name.trim();
    if (step === 4) return !!form.condition;
    if (step === 5) return PHOTO_SLOTS.filter((s) => s.required).every((s) => photos[s.key]);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        purchase_year: form.purchase_year ? parseInt(form.purchase_year) : null,
        years_used: form.years_used ? parseFloat(form.years_used) : null,
        times_worn: form.times_worn ? parseInt(form.times_worn) : null,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        original_mrp: form.original_mrp ? parseFloat(form.original_mrp) : null,
        photos: Object.values(photos),
      };
      const { data } = await api.post("/trade-ins", payload);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="tradein-success">
        <CheckCircle2 size={44} strokeWidth={1} className="mx-auto text-gold" />
        <h1 className="font-serif text-5xl tracking-tight mt-8">Your Trade-In has been submitted.</h1>
        <p className="mt-4 font-mono text-lg" data-testid="tradein-id">{result.trade_in_id}</p>
        <p className="mt-2 text-sm text-muted-foreground">Status: <span className="text-wine">{result.status}</span></p>
        <p className="mt-6 text-ink/60 max-w-md mx-auto text-sm leading-relaxed">
          Our team will review your photographs and details, then prepare a Cloth Cash offer. We'll never quote a value before review — and this garment will never return to our shopping catalog.
        </p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/account?tab=submissions" data-testid="view-my-tradeins" className="bg-ink text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase">My Submissions</Link>
          <Link to="/shop" data-testid="continue-shopping" className="border border-ink px-8 py-4 text-[11px] tracking-[0.25em] uppercase">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  const inputCls = "w-full bg-transparent border border-border px-4 py-3.5 text-sm outline-none focus:border-wine transition-colors placeholder:text-muted-foreground/60";

  return (
    <div className="max-w-3xl mx-auto px-5 py-14 md:py-20" data-testid="tradein-flow">
      <Overline>Trade-In · Step {step + 1} of 6</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">{STEP_LABELS[step]}</h1>
      <div className="mt-6 flex gap-1.5" data-testid="tradein-progress">
        {STEP_LABELS.map((s, i) => (
          <span key={s} className={`h-0.5 flex-1 transition-colors duration-500 ${i <= step ? "bg-wine" : "bg-border"}`} />
        ))}
      </div>

      <div className="mt-12 min-h-[380px]">
        {step === 0 && (
          <div className="grid grid-cols-3 gap-4">
            {DEPARTMENTS.map((d) => (
              <button key={d.key} onClick={() => set("gender", d.key)} data-testid={`ti-gender-${d.key}`}
                className={`aspect-square border flex flex-col items-center justify-center gap-2 transition-colors ${form.gender === d.key ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                <span className="font-serif text-2xl md:text-3xl">{d.label}</span>
              </button>
            ))}
          </div>
        )}
        {step === 1 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {ITEM_TYPES.map((t) => (
              <button key={t} onClick={() => set("item_type", t)} data-testid={`ti-type-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`border px-4 py-4 text-sm tracking-wide transition-colors ${form.item_type === t ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                {t}
              </button>
            ))}
          </div>
        )}
        {step === 2 && (
          <div className="space-y-8">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Select Brand</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
                {[...brands.filter((b) => b.trade_in_eligible), { name: "Other Brand", slug: "other" }].map((b) => (
                  <button key={b.slug} onClick={() => { set("brand_selected", b.name); if (b.slug !== "other") set("brand_name", b.name); else set("brand_name", ""); }}
                    data-testid={`ti-brand-${b.slug}`}
                    className={`border px-3 py-3 text-sm text-left transition-colors ${form.brand_selected === b.name ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                    {b.name}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Not every brand is automatically eligible — eligibility is confirmed during review.</p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Brand Name *</p>
              <input value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} data-testid="ti-brand-name"
                placeholder="Enter brand name exactly as on the label" className={inputCls} />
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="ti-details">
            {[["size", "Size"], ["color", "Color"], ["material", "Material"], ["purchase_year", "Approximate Purchase Year"], ["years_used", "Approximate Years Used"], ["times_worn", "Times Worn (optional)"], ["purchase_price", "Original Purchase Price ₹ (optional)"], ["original_mrp", "Original MRP ₹ (optional)"]].map(([k, label]) => (
              <div key={k}>
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">{label}</p>
                <input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`ti-${k.replace(/_/g, "-")}`} className={inputCls} />
              </div>
            ))}
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3">
            {CONDITIONS.map((c) => (
              <button key={c.key} onClick={() => set("condition", c.key)} data-testid={`ti-condition-${c.key.toLowerCase().replace(/ /g, "-")}`}
                className={`w-full text-left border p-5 transition-colors ${form.condition === c.key ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                <p className="font-serif text-xl">{c.key}</p>
                <p className={`mt-1 text-sm ${form.condition === c.key ? "text-ivory/60" : "text-muted-foreground"}`}>{c.desc}</p>
              </button>
            ))}
            <textarea value={form.condition_notes} onChange={(e) => set("condition_notes", e.target.value)} data-testid="ti-condition-notes"
              placeholder="Anything else we should know? Stains, alterations, repairs…" rows={3} className={`${inputCls} mt-4`} />
          </div>
        )}
        {step === 5 && (
          <div>
            <p className="text-sm text-ink/60 mb-6 flex items-center gap-2"><Camera size={16} strokeWidth={1.5} /> Clear photographs help us evaluate your item accurately.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {PHOTO_SLOTS.map((slot) => (
                <div key={slot.key}>
                  <input ref={(el) => (fileRefs.current[slot.key] = el)} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => readPhoto(slot.key, e.target.files[0])} data-testid={`ti-photo-input-${slot.key}`} />
                  {photos[slot.key] ? (
                    <div className="relative aspect-[3/4] group" data-testid={`ti-photo-preview-${slot.key}`}>
                      <img src={photos[slot.key]} alt={slot.label} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-ink/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button onClick={() => fileRefs.current[slot.key]?.click()} data-testid={`ti-photo-replace-${slot.key}`}
                          className="text-ivory text-[10px] tracking-[0.15em] uppercase border border-ivory/60 px-3 py-1.5">Replace</button>
                        <button onClick={() => setPhotos((p) => { const n = { ...p }; delete n[slot.key]; return n; })} data-testid={`ti-photo-remove-${slot.key}`}
                          className="text-ivory border border-ivory/60 p-1.5" aria-label="Remove"><X size={13} /></button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => fileRefs.current[slot.key]?.click()} data-testid={`ti-photo-add-${slot.key}`}
                      className="w-full aspect-[3/4] border border-dashed border-border hover:border-wine transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground p-3 text-center">
                      <Plus size={18} strokeWidth={1.5} />
                      <span className="text-[10px] tracking-[0.15em] uppercase leading-relaxed">{slot.label}{slot.required && " *"}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-12 flex justify-between">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="ti-back-btn"
          className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase disabled:opacity-30">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back
        </button>
        {step < 5 ? (
          <button onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()} data-testid="ti-next-btn"
            className="bg-ink text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase flex items-center gap-3 disabled:opacity-30 hover:bg-wine transition-colors">
            Continue <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        ) : (
          <button onClick={submit} disabled={!canNext() || submitting} data-testid="ti-submit-btn"
            className="bg-wine text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase disabled:opacity-30 hover:bg-ink transition-colors">
            {submitting ? "Submitting…" : "Submit for Review"}
          </button>
        )}
      </div>
      <p className="mt-8 text-[11px] text-muted-foreground leading-relaxed">
        Cloth Cash is calculated as a share of the approved Trade-In value after our review — no value is guaranteed before review.
        Statuses you'll see: {TRADE_IN_STEPS.join(" · ")}
      </p>
    </div>
  );
}
